import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import NumberFormat from 'react-number-format';
import NumPad from './NumPad';
import { createPortal } from 'react-dom';
import debounce from 'lodash.debounce';
import NumberFormatTypes from './constants';

/**
 * Because 0.1 + 0.2 !== 0.3
 * @param v1
 * @param v2
 * @param decimalScale
 * @returns {boolean}
 */
function valuesAreEqual(v1, v2, decimalScale = 0) {
  function getValueString(maybeNumber) {
    return Number.isFinite(maybeNumber)
      ? maybeNumber.toFixed(decimalScale)
      : null;
  }

  return getValueString(v1) === getValueString(v2);
}

function spinStep(isSubtracting, stepToUse, min, max, initialValue) {
  let newValue = isSubtracting
    ? (initialValue || 0) - stepToUse
    : (initialValue || 0) + stepToUse;
  if (isSubtracting && (min || min === 0) && newValue < min) {
    newValue = min;
  } else if (!isSubtracting && (max || max === 0) && newValue > max) {
    newValue = max;
  }

  return newValue;
}

/**
 * This component allows multiple input mechanisms for entering a number.
 * This component is a controlled component that maintains its internal state.
 * If the value property changes, the component will update its internal state to match.
 *
 * The 3 primary mechanisms for entering a numerical value are:
 *
 * 1) A numeric input field.  It formats as you type to enhance cognition.
 * 2) A spinner (+/- buttons) that allow you to increment or decrement by a fixed amount.
 * 3) A numpad, similar to the native UI for a tablet.
 *
 * Spinner and numerical inputs are debounced so valueChange events will be fired based on the specified delay.
 * @param props
 * @returns {*}
 * @constructor
 */
/* eslint-disable react/prop-types */
export const NumberInput = (props) => {
  const {
    className,
    value,
    step = 1,
    min,
    max,
    format = 'number',
    onChange,
    plus = <span>+</span>,
    minus = <span>-</span>,
    decimalScale = 0,
    thousandSeparator = true,
    pad = <span>⌨</span>,
    padElementId = false,
    header = '',
    delay = 500,
    placeholder,
  } = props;

  const cssBEM = 'numberFormatComponent';

  const [showPad, setShowPad] = useState(false);

  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    if (
      !spinStatusRef.current.spinning &&
      !valuesAreEqual(value, currentValue, decimalScale)
    ) {
      setCurrentValue(value);
    }
  }, [value]);

  const spinStatusRef = useRef({
    spinning: false,
    direction: 'up',
    iterations: 0,
    currentDelay: 400,
    timerHandle: undefined,
    startSpinning: (step, direction) => {
      spinStatusRef.current.direction = direction;
      spinStatusRef.current.iterations = 0;
      spinStatusRef.current.currentDelay = 400;
      spinStatusRef.current.spinning = true;
      spinStatusRef.current.step = step;
      spinStatusRef.current.spin();
    },
    stopSpinning: () => {
      spinStatusRef.current.spinning = false;
      if (spinStatusRef.current.timerHandle) {
        clearTimeout(spinStatusRef.current.timerHandle);
        delete spinStatusRef.current.timerHandle;
      }
    },
    spin: () => {
      const { current: spinStatus } = spinStatusRef;

      if (spinStatus.spinning) {
        const newValue = spinStep(
          spinStatus.direction === 'down',
          spinStatus.step,
          min,
          max,
          spinStatus.currentValue
        );
        spinStatus.currentValue = newValue;
        setCurrentValue(newValue);
        spinStatus.iterations++;
        if (spinStatus.iterations % 10 === 0 && spinStatus.step < 1000000) {
          const newStep = spinStatus.step * 10;
          spinStatus.step = newStep;
        }
        spinStatus.timerHandle = setTimeout(
          spinStatus.spin,
          spinStatus.currentDelay
        );
        if (spinStatus.currentDelay > 100) {
          spinStatus.currentDelay -= 50;
        }
      }
    },
    currentValue,
  });

  //Because you need only one of these per instance of the component.
  const debouncedChange = useRef(
    debounce((oldValue, newValue, decimalScale) => {
      if (!valuesAreEqual(oldValue, newValue, decimalScale)) {
        onChange(newValue);
      }
    }, delay)
  );

  //We expect this to be a numeric value.  Keeping it a number throughout makes this easier to deal with.
  const valueChanged = (newValue, debounce = false) => {
    setCurrentValue(newValue);
    spinStatusRef.current.currentValue = newValue;
    if (debounce) {
      debouncedChange.current(value, newValue, decimalScale);
    } else {
      onChange(newValue);
    }
  };

  const onChangeCallback = (values) => {
    valueChanged(values.floatValue, true);
  };

  const onPadChange = (newValue) => {
    setShowPad(false);
    valueChanged(newValue);
  };

  const startSpinning = (direction) => {
    spinStatusRef.current.startSpinning(step, direction);
  };

  const stopSpinning = () => {
    spinStatusRef.current.stopSpinning();
  };

  const handlePlusMinusKeyDown = (event, direction) => {
    if (event.key === 'Enter' || event.key === ' ') {
      spinStatusRef.current.startSpinning(step, direction);
    }
  };

  const prefix = format === NumberFormatTypes.CURRENCY ? '$' : '';
  const suffix = format === NumberFormatTypes.PERCENT ? '%' : '';

  const minusButton = minus ? (
    <button
      className={`${cssBEM}__button ${cssBEM}__button--minus`}
      disabled={min !== undefined && value <= min}
      onMouseDown={() => startSpinning('down')}
      onMouseUp={stopSpinning}
      onKeyDown={() => handlePlusMinusKeyDown(event, 'down')}
      onKeyUp={stopSpinning}
      aria-label="Decrement"
      role="button"
    >
      {minus}
    </button>
  ) : null;

  const plusButton = plus ? (
    <button
      className={`${cssBEM}__button ${cssBEM}__button--plus`}
      disabled={max !== undefined && value >= max}
      onMouseDown={() => startSpinning('up')}
      onMouseUp={stopSpinning}
      onKeyDown={() => handlePlusMinusKeyDown(event, 'up')}
      onKeyUp={stopSpinning}
      aria-label="Increment"
      role="button"
    >
      {plus}
    </button>
  ) : null;

  const padButton = pad ? (
    <button
      className={`${cssBEM}__button ${cssBEM}__button--pad`}
      disabled={max !== undefined && value >= max}
      onClick={() => setShowPad(!showPad)}
    >
      {pad}
    </button>
  ) : null;

  const classesObj = {};
  classesObj[`${cssBEM}--invalid`] =
    (min !== undefined && value < min) || (max !== undefined && value > max);
  const classes = classNames(className, cssBEM, classesObj);

  const renderNumPad = () => {
    const numPadRender = (
      <NumPad
        className={`${cssBEM}__pad`}
        header={header}
        value={value}
        format={format}
        thousandSeparator={thousandSeparator}
        onChange={onPadChange}
      />
    );

    if (padElementId) {
      return createPortal(numPadRender, document.getElementById(padElementId));
    } else {
      return numPadRender;
    }
  };

  const inputValue = Number.isFinite(currentValue) ? currentValue : '';

  return (
    <div className={classes}>
      <NumberFormat
        className={`${cssBEM}__input`}
        value={inputValue}
        prefix={prefix}
        suffix={suffix}
        aria-label={header}
        onValueChange={onChangeCallback}
        decimalScale={decimalScale}
        thousandSeparator={thousandSeparator}
        allowLeadingZeros={false}
        placeholder={placeholder}
      />
      {minusButton}
      {plusButton}
      {padButton}
      {showPad ? renderNumPad() : null}
    </div>
  );
};

NumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  format: PropTypes.oneOf([
    NumberFormatTypes.NUMBER,
    NumberFormatTypes.CURRENCY,
    NumberFormatTypes.PERCENT,
  ]),
  onChange: PropTypes.func,
  plus: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
  minus: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
  pad: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
  header: PropTypes.string,
  placeholder: PropTypes.string,
};

export default NumberInput;
